/* import __COLOCATED_TEMPLATE__ from './fin-insights.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';

import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import { inject as service } from '@ember/service';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import { indexBy, isEmpty } from 'underscore';
import { fetchTopicsTaxonomy } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import type {
  Topic,
  TopicTaxonomy,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import {
  jobs,
  jobGroups,
  jobGroupObjects,
  type JobId,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { isPresent } from '@ember/utils';
import ENV from 'embercom/config/environment';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { type StandalonePlatform } from 'embercom/objects/standalone/constants';

interface Args {}

export default class FinInsights extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare permissionsService: any;
  @service declare reportingChartService: ReportingChartService;
  @service declare contentSuggestionsService: ContentSuggestionsService;

  @tracked selectedJobGroup: keyof typeof jobGroups = 'improveAnswers';
  @tracked selectedJobId: JobId = 'identifyFinContentGaps';

  @tracked exploringTopicId: string | null = null;
  @tracked exploringSubtopicId: string | null = null;
  @tracked showSidePanel = false;
  @tracked selectedTopicObject: Topic | null = null;
  @tracked selectedSubTopic: string | null = null;
  @tracked type: 'suggestions' | 'conversations' = 'suggestions';
  @tracked conversationCount = 0;
  @tracked showStandaloneSideDrawer = false;
  @tracked hideRowsWithoutSuggestions = false;
  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  @use topicsTaxonomy = AsyncData<TopicTaxonomy>(({ abortSignal }) =>
    fetchTopicsTaxonomy(
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
      abortSignal,
    ),
  );

  getTopicById(id: string | null): Topic | null {
    if (id === null) {
      return null;
    }
    return (
      this.topicsTaxonomy.value?.[id] ?? {
        name: id,
        description: null,
        subtopics: {},
      }
    );
  }

  get exploringTopic() {
    return this.getTopicById(this.exploringTopicId);
  }

  get serializedReport() {
    return {
      id: 'fin-insights',
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  @cached
  get jobs() {
    return indexBy(jobs(this.intl), (job) => job.id);
  }

  get availableJobIds() {
    return jobGroups[this.selectedJobGroup];
  }

  get availableJobs() {
    return this.availableJobIds.map((id) => this.jobs[id]);
  }

  get selectedJob() {
    return this.jobs[this.selectedJobId];
  }

  get jobGroupObjects() {
    return jobGroupObjects;
  }

  get hasFinLive() {
    if (ENV.environment === 'development') {
      return true;
    } else if (this.appService.app.isStandaloneApp) {
      return this.appService.mainStandalonePlatform.finIsLive;
    }
    return this.aiAgentSetupService.isFinLiveIncludingChannelsOutsideSetup();
  }

  get emptyState() {
    if (this.appService.app.canUseFeature('insights-override-fin-live-empty-states')) {
      return null;
    }
    if (this.shouldShowZendeskConnectScreen) {
      return 'zendesk';
    } else if (this.shouldShowSalesforceConnectScreen) {
      return 'salesforce';
    } else if (this.finIsNotLiveAndIsPaused) {
      return 'fin-paused';
    } else if (!this.hasFinLive) {
      return 'fin-not-live';
    } else {
      return null; // not an empty state - show it!
    }
  }

  get finIsNotLiveAndIsPaused() {
    if (ENV.environment === 'development' || !this.appService.app.isStandaloneApp) {
      return false;
    }
    return this.appService.mainStandalonePlatform.finIsNotLiveAndIsPaused;
  }

  get missingTopicsTaxonomy() {
    return isEmpty(this.topicsTaxonomy.value) && !this.topicsTaxonomy.isLoading;
  }

  get showHeader() {
    return (
      this.hasFinLive && isPresent(this.topicsTaxonomy.value) && !this.topicsTaxonomy.isLoading
    );
  }

  get shouldShowZendeskConnectScreen() {
    if (!this.appService.app.isStandaloneApp || this.reportingChartService.useSyntheticData) {
      return false;
    }

    if (
      this.appService.app.hasStandalonePlatform('zendesk') &&
      !this.appService.mainStandalonePlatform.isConnected
    ) {
      return true;
    }

    return (
      this.appService.app.hasStandalonePlatform('zendesk') &&
      this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta
    );
  }

  get shouldShowSalesforceConnectScreen() {
    if (!this.appService.app.isStandaloneApp || this.reportingChartService.useSyntheticData) {
      return false;
    }

    if (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      !this.appService.mainStandalonePlatform.isConnected
    ) {
      return true;
    }

    return (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta
    );
  }

  @action
  closeStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = false;
  }

  @action
  openStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = true;
  }

  get currentPlatform(): StandalonePlatform {
    return this.appService.mainStandalonePlatform.platformKey;
  }

  @action
  changeJobGroup(jobGroup: keyof typeof jobGroups) {
    this.selectedJobGroup = jobGroup;
    this.selectedJobId = this.availableJobIds[0];
    this.hideRowsWithoutSuggestions = false;
    this.trackAnalyticsEvent('job-group-changed', jobGroup);
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/optimize-fin',
      action,
      object,
      ...extraContext,
    });
  }

  @action
  changeJob(jobId: JobId) {
    this.selectedJobId = jobId;
    this.hideRowsWithoutSuggestions = false;
    this.trackAnalyticsEvent('job-changed', jobId);
  }

  @action
  onShowConversationExplorer(
    topic: string,
    subtopic: string | null,
    type: 'suggestions' | 'conversations',
    conversationCount: number,
  ) {
    if (this.canAccessDrillIn) {
      this.exploringTopicId = topic;
      this.exploringSubtopicId = subtopic;
      this.type = type;
      this.showSidePanel = true;
      this.conversationCount = conversationCount;
      if (type === 'conversations') {
        this.trackAnalyticsEvent('conversation-explorer-opened', 'conversation-explorer', {
          topic,
          subtopic,
        });
      } else {
        this.trackAnalyticsEvent('suggestions-explorer-opened', 'conversation-explorer', {
          topic,
          subtopic,
        });
      }
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  toggleHideRowsWithoutSuggestions() {
    this.hideRowsWithoutSuggestions = !this.hideRowsWithoutSuggestions;
    if (this.hideRowsWithoutSuggestions) {
      this.trackAnalyticsEvent('rows-without-suggestions-hidden', 'metrics-table', {
        hideRowsWithoutSuggestions: this.hideRowsWithoutSuggestions,
      });
    }
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  @action
  closeConversationExplorer() {
    this.showSidePanel = false;
    this.contentSuggestionsService.fetchTopicsSummary();
    this.trackAnalyticsEvent('conversation-explorer-closed', 'conversation-explorer');
  }

  @action
  setSelectedSubTopic(subtopic: string | null) {
    this.exploringSubtopicId = subtopic;
    this.trackAnalyticsEvent('subtopic-back-to-topic', 'conversation-explorer', {
      topic: this.exploringTopicId,
      subtopic,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinInsights': typeof FinInsights;
  }
}
